export type LocalRecordingStatus = 'active' | 'interrupted' | 'completed';

/** Keep synchronized with MAX_RECORDING_DURATION_MS in the recording API. */
export const MAX_RECORDING_DURATION_MS = 30 * 60 * 1_000;

/**
 * How long a walkthrough may spend paused, in all, before it stops itself and
 * files what was recorded. The server gives up on a session that still claims
 * to be recording a while after the longest one could last, counted from when
 * it started, so a pause has to end somewhere. Keep synchronized with
 * MAX_RECORDING_PAUSE_MS in the recording API (a test there reads this file).
 */
export const MAX_RECORDING_PAUSE_MS = 45 * 60 * 1_000;

/** How many of a recording's latest pauses the snapshot carries (`RecorderSnapshot.pauses`). */
export const MAX_RECENT_PAUSES = 20;

/**
 * The server's reasons for refusing a new walkthrough that a person can act on
 * (TOADO-501), each with the status it arrives with. Only these codes travel
 * from a response into the recorder's state and the copy shown for it; any
 * other body stays out, so a server cannot put its own words in front of the
 * person through this path.
 */
export const RECORDING_START_REFUSALS = {
  active_recording_limit: 409,
  recording_not_enabled: 403,
  recording_minutes_exhausted: 402,
  recording_storage_full: 402,
} as const;

export type RecordingStartRefusalCode = keyof typeof RECORDING_START_REFUSALS;

/** The refusal code in a failed start, when it is one of ours with its own status. */
export function recordingStartRefusalCode(status: unknown, code: unknown): RecordingStartRefusalCode | undefined {
  if (typeof code !== 'string' || !Object.prototype.hasOwnProperty.call(RECORDING_START_REFUSALS, code)) return undefined;
  const known = code as RecordingStartRefusalCode;
  return RECORDING_START_REFUSALS[known] === status ? known : undefined;
}

/** The shortest limit a server may set; anything less is treated as a mistake. */
export const MIN_RECORDING_DURATION_LIMIT_MS = 60 * 1_000;

/**
 * How long this walkthrough may run: the server's limit for the workspace's
 * plan (TOADO-496), inside the recorder's own ceiling. A server from before
 * limits existed sends none, and an unusable value is not trusted to shorten a
 * recording to nothing, so both mean the ceiling.
 */
export function recordingDurationLimitMs(limit: unknown): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit) || limit < MIN_RECORDING_DURATION_LIMIT_MS) {
    return MAX_RECORDING_DURATION_MS;
  }
  return Math.min(MAX_RECORDING_DURATION_MS, Math.floor(limit));
}

export type RecordingSegmentType = 'bug' | 'feature_request';
export type RecordingSnapshotTrigger = 'start' | 'finish' | 'manual';

export interface RecordingServerSnapshot {
  id: string;
  segmentId: string;
  requestId?: string;
  capturedAtMs: number;
  trigger: RecordingSnapshotTrigger;
  orderIndex: number;
  screenshotUrl?: string;
  thumbnailUrl?: string;
  sourceUrl?: string;
  uploadStatus?: 'pending' | 'ready' | 'failed';
  attemptCount?: number;
  lastErrorCode?: string;
}

export interface PendingRecordingSnapshot {
  key: string;
  ownerId: string;
  recordingId: string;
  segmentId: string;
  projectId: string;
  requestId: string;
  capturedAtMs: number;
  trigger: RecordingSnapshotTrigger;
  sourceUrl: string;
  screenshot: Blob;
  thumbnail: Blob;
  state: 'pending' | 'uploading' | 'failed';
  attempts: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  lastErrorCode?: string;
}

export interface RecordingServerSegment {
  id: string;
  sessionId: string;
  startMs: number;
  endMs?: number;
  segmentType: RecordingSegmentType;
  droppedAt?: string;
  /**
   * `expired` is a clip pruned once its plan's retention window passed
   * (TOADO-367); it never returns. `expiring` is one being removed right now,
   * and is already unavailable, so readers treat the two alike.
   */
  clipUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed' | 'expired' | 'expiring';
  clipBytes?: number;
  transcriptText?: string;
  transcriptStatus?: 'not_requested' | 'pending' | 'ready' | 'failed';
  transcriptAttemptCount?: number;
  transcriptLastErrorCode?: string;
  transcriptUsageSeconds?: number;
  draftTitle?: string;
  draftDescription?: string;
  snapshots: RecordingServerSnapshot[];
}

export type RecorderPhase =
  | 'idle'
  | 'requesting-display'
  | 'requesting-microphone'
  | 'starting'
  | 'recording'
  | 'stopping'
  | 'completed'
  | 'error';

export type StopReason =
  | 'user'
  | 'display-ended'
  | 'microphone-ended'
  | 'duration-limit'
  | 'pause-limit'
  | 'persistence-error'
  | 'recorder-error'
  /** The server closed the walkthrough while this recorder was away (TOADO-900). */
  | 'closed-by-server';

export interface LocalRecordingSession {
  id: string;
  ownerId: string;
  companyId: string;
  projectId: string;
  status: LocalRecordingStatus;
  /**
   * Screen and narration, or narration alone (TOADO-380).
   *
   * Stored locally so a resume knows what it is resuming BEFORE it awaits
   * anything. `getDisplayMedia` has to be the first awaited call inside the
   * user gesture, so a recovered recording cannot ask the server which mode it
   * was and then decide - and deciding wrong means a share picker in front of
   * somebody who opted out of screen capture. Absent on a row written before
   * the choice existed, which was video.
   */
  mediaMode?: 'video' | 'audio';
  mimeType: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  nextPartIndex: number;
  nextSequence: number;
  elapsedMs: number;
  chunkCount: number;
  storedBytes: number;
  /**
   * The length the server allowed when this walkthrough started (TOADO-496),
   * kept so a resume stops at the same point: the server answers it only on
   * create. Absent on a row from before limits, which ran to the ceiling.
   */
  maxDurationMs?: number;
}

export interface RecordingChunk {
  key: string;
  sessionId: string;
  ownerId: string;
  partIndex: number;
  sequence: number;
  startMs: number;
  endMs: number;
  mimeType: string;
  size: number;
  blob: Blob;
  createdAt: string;
}

export type RecordingSegmentSettlementAction = 'close' | 'cancel';

/**
 * A crash-safe intent to settle an authoritative server segment. The intent is
 * written before the request and removed only after the idempotent endpoint
 * confirms success.
 */
export interface PendingRecordingSegmentSettlement {
  key: string;
  ownerId: string;
  recordingId: string;
  segmentId: string;
  action: RecordingSegmentSettlementAction;
  state: 'pending' | 'terminal' | 'cleanup';
  endMs?: number;
  createdAt: string;
  expiresAt: string;
  lastAttemptAt: string;
  attempts: number;
  lastErrorCode?: string;
}

export interface RecordingServerSession {
  id: string;
  companyId: string;
  projectId: string;
  status: string;
  /**
   * What the server recorded this as (TOADO-380).
   *
   * Checked against what the client asked for rather than trusted blindly:
   * an API from before this existed ignores the field on create and answers
   * `video`, and recording audio into a session the server believes is video
   * ends with a master that has no dimensions and a walkthrough reported as
   * unreadable media. Absent means an older server said nothing.
   */
  mediaMode?: 'video' | 'audio';
  expiresAt: string;
  /**
   * Closed while its recorder was away (TOADO-900): the server is finalising
   * it, and refuses this recorder. Not to be resumed.
   */
  closedToRecorder?: boolean;
  /** Whether its clips can be cut again, as the server decides it (TOADO-900). */
  clipsRetryable?: boolean;
  /**
   * Sent on create only (TOADO-496): how long this walkthrough may run under
   * the workspace's plan. Read through `recordingDurationLimitMs`.
   */
  limits?: { maxDurationMs?: number };
  masterUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed';
  masterUploadOffset?: number;
  masterUploadTotalBytes?: number;
  /** Last server-side change. Used to tell stuck work from ongoing work. */
  updatedAt?: string;
  /** Worker claim on a processing session; absent once it settles. */
  processingLeaseExpiresAt?: string;
  /**
   * True while a worker is actually running this session, from the writer
   * lease it heartbeats. Not derivable from `processingLeaseExpiresAt`: the
   * retry route pushes that thirty minutes out itself on enqueue.
   */
  workerActive?: boolean;
  segments?: RecordingServerSegment[];
}

export type RecordingUploadStatus =
  | 'pending'
  | 'uploading'
  | 'processing'
  | 'cleanup'
  | 'ready'
  | 'failed';

export interface LocalRecordingUpload {
  recordingId: string;
  ownerId: string;
  status: RecordingUploadStatus;
  offset: number;
  totalBytes: number;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  lastErrorCode?: string;
  /**
   * The server's own sentence for a refusal a retry cannot fix.
   *
   * Only written for terminal failures, and only when the server sent one.
   * A quota refusal knows the cap, what is used and how many moments this
   * walkthrough marked; restating that from a code on the client would mean
   * keeping a second copy of the plan rules in the recorder.
   */
  lastErrorMessage?: string;
  /**
   * The part size the master is being sent in, while a slow line has made it
   * smaller than the largest (TOADO-991). On the row rather than in one
   * window, because every window shares the attempts it is spent against.
   */
  partBytes?: number;
  /**
   * The sizes already sent at the offset being uploaded (TOADO-991). The
   * server keeps a reservation for a part whose store failed, and takes only
   * those bytes there afterwards, so a refused part is sent again in these.
   */
  partTried?: { start: number; sizes: number[] };
  /**
   * How long a byte has taken to send, for the last few parts to land, each
   * with when it was measured (TOADO-991, 28 Oct): what a part's deadline
   * follows. Only recent ones are used: an upload resumed much later may be on
   * another line (review round 1).
   */
  partRates?: Array<{ at: number; msPerByte: number }>;
  /**
   * Parts in a row that have timed out under a deadline the measurements set,
   * since one last landed (review round 2): each doubles the next deadline, up
   * to the fixed one. Not a measurement: a held request times out on a fast line too.
   */
  partStretch?: number;
}

export type MicrophoneState =
  | { kind: 'idle'; level: 0 }
  | { kind: 'requesting'; level: 0 }
  | { kind: 'ready'; level: number; deviceId: string }
  | { kind: 'denied'; level: 0 }
  | { kind: 'unavailable'; level: 0 }
  | { kind: 'error'; level: 0 };

export interface RecorderSnapshot {
  phase: RecorderPhase;
  session: LocalRecordingSession | null;
  recoveries: LocalRecordingSession[];
  microphone: MicrophoneState;
  elapsedMs: number;
  /**
   * The walkthrough is recording but paused: the media and `elapsedMs` stand
   * still until it resumes. Only ever true in the `recording` phase; absent
   * means not paused.
   */
  paused?: boolean;
  /**
   * This recording's recent pauses on the wall clock (`Date.now()`), oldest
   * first, the open one without `to`. A moment dated by the wall clock
   * elsewhere - a click in a page - is turned into a media time by leaving
   * these out (`mediaTimeAt` in the extension's pin writer). At most
   * MAX_RECENT_PAUSES: a click is answered in seconds, not after twenty pauses.
   */
  pauses?: ReadonlyArray<{ from: number; to?: number }>;
  /**
   * From when on the wall clock `pauses` is complete: the recording's start,
   * or the end of the latest pause dropped to keep the list short. A moment
   * before it cannot be placed, because a pause it needs may be missing.
   */
  pausesFrom?: number;
  stopReason: StopReason | null;
  errorCode: string | null;
  cleanupPending: boolean;
}

export interface RecorderStore {
  saveSession(session: LocalRecordingSession): Promise<void>;
  appendChunk(session: LocalRecordingSession, chunk: RecordingChunk, signal?: AbortSignal): Promise<void>;
  listRecoverable(ownerId: string, now: Date): Promise<LocalRecordingSession[]>;
  markStatus(id: string, status: LocalRecordingStatus, elapsedMs?: number): Promise<LocalRecordingSession | null>;
  deleteSession(id: string): Promise<void>;
}

export interface RecorderUploadStore {
  readChunks(recordingId: string): Promise<RecordingChunk[]>;
  deleteChunks(recordingId: string): Promise<void>;
  saveUpload(upload: LocalRecordingUpload): Promise<void>;
  getUpload(recordingId: string): Promise<LocalRecordingUpload | null>;
  listUploads(ownerId: string): Promise<LocalRecordingUpload[]>;
  deleteUpload(recordingId: string): Promise<void>;
  listRecoverable(ownerId: string, now: Date): Promise<LocalRecordingSession[]>;
}

export interface RecordingSegmentSettlementStore {
  putSegmentSettlement(settlement: PendingRecordingSegmentSettlement): Promise<void>;
  putSegmentSettlementIfState(
    settlement: PendingRecordingSegmentSettlement,
    expectedState: PendingRecordingSegmentSettlement['state'] | null,
  ): Promise<boolean>;
  deleteSegmentSettlement(key: string): Promise<void>;
  listSegmentSettlementsForRecording(recordingId: string): Promise<PendingRecordingSegmentSettlement[]>;
  listSegmentSettlementsForOwner(ownerId: string): Promise<PendingRecordingSegmentSettlement[]>;
  deleteSegmentSettlementsForRecording(recordingId: string): Promise<void>;
  pruneExpiredSegmentSettlements(now: Date): Promise<void>;
}

export interface RecordingSnapshotStore {
  putSnapshot(snapshot: PendingRecordingSnapshot): Promise<void>;
  getSnapshot(key: string): Promise<PendingRecordingSnapshot | null>;
  deleteSnapshot(key: string): Promise<void>;
  listSnapshotsForOwner(ownerId: string): Promise<PendingRecordingSnapshot[]>;
  listSnapshotsForRecording(recordingId: string): Promise<PendingRecordingSnapshot[]>;
  deleteSnapshotsForOwner(ownerId: string): Promise<void>;
  deleteSnapshotsForRecording(recordingId: string): Promise<void>;
  pruneExpiredSnapshots(now: Date): Promise<void>;
}

export interface RecordingSnapshotServerApi {
  preflight(
    recordingId: string,
    signal?: AbortSignal,
    purpose?: 'capture' | 'upload',
  ): Promise<{ captureMode: 'full' | 'metadata_only' | 'never' }>;
  upload(
    snapshot: PendingRecordingSnapshot,
    signal?: AbortSignal,
  ): Promise<RecordingServerSnapshot>;
}

export interface RecorderServerApi {
  /**
   * The mode is optional so an older client means video, which is what
   * every caller meant before there was a choice (TOADO-380).
   */
  create(projectId: string, mediaMode?: 'video' | 'audio'): Promise<RecordingServerSession>;
  get(id: string): Promise<RecordingServerSession>;
  discard(id: string): Promise<void>;
  /**
   * The recorder is recording this walkthrough (TOADO-900): sent when it starts
   * or resumes and every few minutes while it records, so the server does not
   * take a walkthrough resumed after a crash for abandoned. Optional; never
   * fatal.
   */
  alive?(id: string): Promise<void>;
}

export interface RecordingSegmentServerApi {
  get(recordingId: string): Promise<RecordingServerSession>;
  open(
    recordingId: string,
    input: { startMs: number; segmentType: RecordingSegmentType },
  ): Promise<RecordingServerSegment>;
  close(
    recordingId: string,
    segmentId: string,
    endMs: number,
    signal?: AbortSignal,
  ): Promise<RecordingServerSegment>;
  cancel(recordingId: string, segmentId: string, signal?: AbortSignal): Promise<void>;
}
